import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import type {
  ChildExtracurricular,
  ChildFeeAccount,
  ChildFeePayment,
  ChildReportCard,
  ChildSchoolFees,
  DayAttendanceType,
  FeeAccountCategory,
  FeeBillingPattern,
  GuardianRole,
  ParentChild,
} from "../../types/parentFamily";
import { createFamilyId } from "../../utils/parentFamilyStorage";
import { generateStudentId } from "../../utils/parentFamilyHelpers";
import {
  mapFamilyMemberFromApi,
  mergeSchoolFeesFromPatch,
  toFamilyMemberWriteBody,
} from "../../utils/familyApiMappers";
import { amountOutstanding as feeAccountOutstanding } from "../../utils/feeAccountHelpers";
import { upsertPaymentInList } from "../../utils/familyPaymentUpsert";
import { amountOutstanding, isFeesSettled } from "../../utils/schoolFeesHelpers";
import { api } from "../../services/api";

type ExtracurricularInput = Omit<
  ChildExtracurricular,
  "id" | "createdAt" | "updatedAt"
> & { id?: string };

function applyActivityToChild(
  child: ParentChild,
  activity: ExtracurricularInput,
  now: string
): ParentChild {
  const items = [...child.extracurriculars];
  const idx = activity.id ? items.findIndex((a) => a.id === activity.id) : -1;

  if (idx >= 0) {
    items[idx] = {
      ...items[idx],
      ...activity,
      id: items[idx].id,
      updatedAt: now,
    };
  } else {
    items.push({
      ...activity,
      id: createFamilyId(),
      createdAt: now,
      updatedAt: now,
    } as ChildExtracurricular);
  }

  return {
    ...child,
    extracurriculars: items,
    updatedAt: now,
  };
}

interface UseFamilyMutationsArgs {
  orgId: string;
  userId: string;
  token: string | undefined;
  children: ParentChild[];
  setChildrenCache: (updater: (prev: ParentChild[]) => ParentChild[]) => void;
  invalidate: () => Promise<void>;
  runMutation: <T,>(fn: () => Promise<T>) => Promise<T>;
}

export function useFamilyMutations({
  orgId,
  userId,
  token,
  children,
  setChildrenCache,
  invalidate,
  runMutation,
}: UseFamilyMutationsArgs) {
  const patchMember = useCallback(
    async (child: ParentChild) => {
      if (!orgId || !token) {
        throw new Error("Missing organization or auth token");
      }
      const updated = await api.family.updateMember(
        orgId,
        child.id,
        token,
        toFamilyMemberWriteBody(child)
      );
      const mapped = mapFamilyMemberFromApi(updated);
      const normalized = {
        ...mapped,
        schoolFees: mergeSchoolFeesFromPatch(mapped.schoolFees, child.schoolFees),
        feeAccounts:
          mapped.feeAccounts.length > 0
            ? mapped.feeAccounts
            : child.feeAccounts ?? [],
      };
      setChildrenCache((prev) => {
        const idx = prev.findIndex((c) => c.id === normalized.id);
        if (idx < 0) return [...prev, normalized];
        const next = [...prev];
        next[idx] = normalized;
        return next;
      });
      return normalized;
    },
    [orgId, token, setChildrenCache]
  );

  const savingMutation = useMutation({
    mutationFn: async (fn: () => Promise<void>) => runMutation(fn),
  });

  const upsertChild = useCallback(
    async (
      input: Omit<
        ParentChild,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "schoolFees"
        | "feeAccounts"
        | "extracurriculars"
        | "reportCards"
        | "linkedCircleIds"
      > & {
        id?: string;
        schoolFees?: ChildSchoolFees;
        feeAccounts?: ChildFeeAccount[];
        extracurriculars?: ChildExtracurricular[];
        reportCards?: ChildReportCard[];
        linkedCircleIds?: string[];
      }
    ) => {
      await savingMutation.mutateAsync(async () => {
        if (!orgId || !userId || !token) {
          throw new Error("Missing organization or auth token");
        }
        if (input.id) {
          const existing = children.find((c) => c.id === input.id);
          if (!existing) {
            throw new Error("Family member not found");
          }
          await patchMember({
            ...existing,
            ...input,
            id: existing.id,
            schoolFees: input.schoolFees ?? existing.schoolFees,
            feeAccounts: input.feeAccounts ?? existing.feeAccounts,
            extracurriculars:
              input.extracurriculars ?? existing.extracurriculars,
            reportCards: input.reportCards ?? existing.reportCards ?? [],
            linkedCircleIds: input.linkedCircleIds ?? existing.linkedCircleIds,
          });
        } else {
          const created = await api.family.createMember(orgId, userId, token, {
            firstName: input.firstName.trim(),
            lastName: input.lastName.trim(),
            dateOfBirth: input.dateOfBirth,
            gender: input.gender,
            grade: input.grade,
            classSection: input.classSection,
            studentId: input.studentId?.trim() || generateStudentId(),
            notes: input.notes,
            status: input.status || "active",
            linkedCircleIds: input.linkedCircleIds ?? [],
            schoolFees: input.schoolFees,
            feeAccounts: input.feeAccounts ?? [],
            extracurriculars: input.extracurriculars ?? [],
            reportCards: input.reportCards ?? [],
          });
          setChildrenCache((prev) => [...prev, mapFamilyMemberFromApi(created)]);
        }
      });
    },
    [
      savingMutation,
      orgId,
      userId,
      token,
      children,
      patchMember,
      setChildrenCache,
    ]
  );

  const deleteChild = useCallback(
    async (childId: string) => {
      await savingMutation.mutateAsync(async () => {
        if (!orgId || !token) {
          throw new Error("Missing organization or auth token");
        }
        await api.family.deleteMember(orgId, childId, token);
        setChildrenCache((prev) => prev.filter((c) => c.id !== childId));
      });
    },
    [savingMutation, orgId, token, setChildrenCache]
  );

  const updateChildCircles = useCallback(
    async (childId: string, linkedCircleIds: string[]) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, linkedCircleIds });
      });
    },
    [children, patchMember, savingMutation]
  );

  const upsertSchoolFees = useCallback(
    async (
      childId: string,
      input: {
        academicYear: string;
        totalAnnualFee: number;
        currency?: string;
        paymentPlanMonths: number;
        dayType?: DayAttendanceType;
      }
    ) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      const existing = child.schoolFees;
      const account: ChildSchoolFees = {
        id: existing?.id || createFamilyId(),
        academicYear: input.academicYear.trim(),
        totalAnnualFee: input.totalAnnualFee,
        currency: input.currency?.trim() || "ZAR",
        paymentPlanMonths: input.paymentPlanMonths,
        dayType: input.dayType,
        payments: existing?.payments || [],
        feesPaidInFull:
          existing?.feesPaidInFull ||
          (existing ? amountOutstanding(existing) <= 0 : false),
        lastStatementSentAt: existing?.lastStatementSentAt,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      };

      if (amountOutstanding(account) <= 0) {
        account.feesPaidInFull = true;
      }

      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, schoolFees: account, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const recordPayment = useCallback(
    async (
      childId: string,
      payment: Omit<ChildFeePayment, "id" | "createdAt" | "updatedAt"> & {
        id?: string;
      }
    ) => {
      const child = children.find((c) => c.id === childId);
      if (!child?.schoolFees) {
        throw new Error("Set up school fees before recording a payment.");
      }
      const now = new Date().toISOString();
      const payments = upsertPaymentInList(child.schoolFees.payments, payment, now);

      const account: ChildSchoolFees = {
        ...child.schoolFees,
        payments,
        feesPaidInFull: false,
        updatedAt: now,
      };

      if (isFeesSettled(account)) {
        account.feesPaidInFull = true;
      }

      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, schoolFees: account, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const deletePayment = useCallback(
    async (childId: string, paymentId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child?.schoolFees) return;
      const now = new Date().toISOString();
      const account: ChildSchoolFees = {
        ...child.schoolFees,
        payments: child.schoolFees.payments.filter((p) => p.id !== paymentId),
        feesPaidInFull: false,
        updatedAt: now,
      };

      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, schoolFees: account, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const deleteSchoolFees = useCallback(
    async (childId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      await savingMutation.mutateAsync(async () => {
        if (!orgId || !token) {
          throw new Error("Missing organization or auth token");
        }
        const updated = await api.family.updateMember(
          orgId,
          child.id,
          token,
          {
            ...toFamilyMemberWriteBody(child),
            schoolFees: null,
          }
        );
        const mapped = mapFamilyMemberFromApi(updated);
        const normalized = {
          ...mapped,
          schoolFees: undefined,
        };
        setChildrenCache((prev) => {
          const idx = prev.findIndex((c) => c.id === normalized.id);
          if (idx < 0) return [...prev, normalized];
          const next = [...prev];
          next[idx] = normalized;
          return next;
        });
      });
    },
    [children, orgId, token, savingMutation, setChildrenCache]
  );

  const upsertFeeAccount = useCallback(
    async (
      childId: string,
      input: {
        id?: string;
        title: string;
        category: FeeAccountCategory;
        billingPattern: FeeBillingPattern;
        currency?: string;
        startDate: string;
        endDate?: string;
        totalAmount?: number;
        dueDate?: string;
        monthlyAmount?: number;
        linkedActivityId?: string;
      }
    ) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      const accounts = [...child.feeAccounts];
      const idx = input.id ? accounts.findIndex((a) => a.id === input.id) : -1;
      const existing = idx >= 0 ? accounts[idx] : undefined;

      const account: ChildFeeAccount = {
        id: existing?.id || createFamilyId(),
        title: input.title.trim(),
        category: input.category,
        billingPattern: input.billingPattern,
        currency: input.currency?.trim() || "ZAR",
        startDate: input.startDate,
        endDate: input.endDate?.trim() || undefined,
        totalAmount: input.totalAmount,
        dueDate: input.dueDate?.trim() || undefined,
        monthlyAmount: input.monthlyAmount,
        linkedActivityId: input.linkedActivityId || existing?.linkedActivityId,
        payments: existing?.payments || [],
        paidInFull: existing?.paidInFull || false,
        lastStatementSentAt: existing?.lastStatementSentAt,
        cancelledAt: existing?.cancelledAt,
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      };

      if (feeAccountOutstanding(account) <= 0 && (account.payments.length > 0 || account.paidInFull)) {
        account.paidInFull = true;
      }

      if (idx >= 0) {
        accounts[idx] = account;
      } else {
        accounts.push(account);
      }

      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts: accounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const deleteFeeAccount = useCallback(
    async (childId: string, accountId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) return;
      const now = new Date().toISOString();
      await savingMutation.mutateAsync(async () => {
        await patchMember({
          ...child,
          feeAccounts: child.feeAccounts.filter((a) => a.id !== accountId),
          updatedAt: now,
        });
      });
    },
    [children, patchMember, savingMutation]
  );

  const cancelFeeAccount = useCallback(
    async (childId: string, accountId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) return;
      const now = new Date().toISOString();
      const today = now.slice(0, 10);
      const accounts = child.feeAccounts.map((a) =>
        a.id === accountId
          ? { ...a, cancelledAt: today, endDate: a.endDate || today, updatedAt: now }
          : a
      );
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts: accounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const recordFeeAccountPayment = useCallback(
    async (
      childId: string,
      accountId: string,
      payment: Omit<ChildFeePayment, "id" | "createdAt" | "updatedAt"> & {
        id?: string;
      }
    ) => {
      const child = children.find((c) => c.id === childId);
      const account = child?.feeAccounts.find((a) => a.id === accountId);
      if (!child || !account) {
        throw new Error("Fee account not found");
      }
      const now = new Date().toISOString();
      const payments = upsertPaymentInList(account.payments, payment, now);

      const updatedAccount: ChildFeeAccount = {
        ...account,
        payments,
        paidInFull: false,
        updatedAt: now,
      };
      if (feeAccountOutstanding(updatedAccount) <= 0) {
        updatedAccount.paidInFull = true;
      }

      const feeAccounts = child.feeAccounts.map((a) =>
        a.id === accountId ? updatedAccount : a
      );

      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const deleteFeeAccountPayment = useCallback(
    async (childId: string, accountId: string, paymentId: string) => {
      const child = children.find((c) => c.id === childId);
      const account = child?.feeAccounts.find((a) => a.id === accountId);
      if (!child || !account) return;
      const now = new Date().toISOString();
      const updatedAccount: ChildFeeAccount = {
        ...account,
        payments: account.payments.filter((p) => p.id !== paymentId),
        paidInFull: false,
        updatedAt: now,
      };
      const feeAccounts = child.feeAccounts.map((a) =>
        a.id === accountId ? updatedAccount : a
      );
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const markFeeAccountStatementSent = useCallback(
    async (childId: string, accountId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) return;
      const now = new Date().toISOString();
      const feeAccounts = child.feeAccounts.map((a) =>
        a.id === accountId
          ? { ...a, lastStatementSentAt: now, updatedAt: now }
          : a
      );
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const setFeeAccountPaidInFull = useCallback(
    async (childId: string, accountId: string, paidInFull: boolean) => {
      const child = children.find((c) => c.id === childId);
      if (!child) return;
      const now = new Date().toISOString();
      const feeAccounts = child.feeAccounts.map((a) =>
        a.id === accountId ? { ...a, paidInFull, updatedAt: now } : a
      );
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, feeAccounts, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const markStatementSent = useCallback(
    async (childId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child?.schoolFees) return;
      const now = new Date().toISOString();
      await savingMutation.mutateAsync(async () => {
        await patchMember({
          ...child,
          schoolFees: {
            ...child.schoolFees!,
            lastStatementSentAt: now,
            updatedAt: now,
          },
          updatedAt: now,
        });
      });
    },
    [children, patchMember, savingMutation]
  );

  const setFeesPaidInFull = useCallback(
    async (childId: string, paidInFull: boolean) => {
      const child = children.find((c) => c.id === childId);
      if (!child?.schoolFees) return;
      const now = new Date().toISOString();
      await savingMutation.mutateAsync(async () => {
        await patchMember({
          ...child,
          schoolFees: {
            ...child.schoolFees!,
            feesPaidInFull: paidInFull,
            updatedAt: now,
          },
          updatedAt: now,
        });
      });
    },
    [children, patchMember, savingMutation]
  );

  const upsertExtracurricular = useCallback(
    async (childId: string, activity: ExtracurricularInput) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      const next = applyActivityToChild(child, activity, now);
      await savingMutation.mutateAsync(async () => {
        await patchMember(next);
      });
    },
    [children, patchMember, savingMutation]
  );

  const addExtracurriculars = useCallback(
    async (childId: string, activities: ExtracurricularInput[]) => {
      if (activities.length === 0) return;
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      const next = activities.reduce(
        (acc, activity) => applyActivityToChild(acc, activity, now),
        child
      );
      await savingMutation.mutateAsync(async () => {
        await patchMember(next);
      });
    },
    [children, patchMember, savingMutation]
  );

  const deleteExtracurricular = useCallback(
    async (childId: string, activityId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      await savingMutation.mutateAsync(async () => {
        await patchMember({
          ...child,
          extracurriculars: child.extracurriculars.filter(
            (a) => a.id !== activityId
          ),
          updatedAt: now,
        });
      });
    },
    [children, patchMember, savingMutation]
  );

  const upsertReportCard = useCallback(
    async (
      childId: string,
      report: Omit<ChildReportCard, "id" | "createdAt" | "updatedAt"> & {
        id?: string;
      }
    ) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      const cards = [...(child.reportCards || [])];
      const idx = report.id ? cards.findIndex((r) => r.id === report.id) : -1;
      if (idx >= 0) {
        cards[idx] = {
          ...cards[idx],
          ...report,
          id: cards[idx].id,
          updatedAt: now,
        };
      } else {
        cards.push({
          ...report,
          id: createFamilyId(),
          createdAt: now,
          updatedAt: now,
        } as ChildReportCard);
      }
      await savingMutation.mutateAsync(async () => {
        await patchMember({ ...child, reportCards: cards, updatedAt: now });
      });
    },
    [children, patchMember, savingMutation]
  );

  const deleteReportCard = useCallback(
    async (childId: string, reportId: string) => {
      const child = children.find((c) => c.id === childId);
      if (!child) {
        throw new Error("Family member not found");
      }
      const now = new Date().toISOString();
      await savingMutation.mutateAsync(async () => {
        await patchMember({
          ...child,
          reportCards: (child.reportCards || []).filter((r) => r.id !== reportId),
          updatedAt: now,
        });
      });
    },
    [children, patchMember, savingMutation]
  );

  const addGuardian = useCallback(
    async (
      memberId: string,
      guardianUserId: string,
      role: GuardianRole = "co-parent"
    ) => {
      await savingMutation.mutateAsync(async () => {
        if (!orgId || !token) {
          throw new Error("Missing organization or auth token");
        }
        const updated = await api.family.addGuardian(orgId, memberId, token, {
          userId: guardianUserId,
          role,
        });
        const mapped = mapFamilyMemberFromApi(updated);
        setChildrenCache((prev) => {
          const idx = prev.findIndex((c) => c.id === mapped.id);
          if (idx < 0) return [...prev, mapped];
          const next = [...prev];
          next[idx] = mapped;
          return next;
        });
      });
    },
    [savingMutation, orgId, token, setChildrenCache]
  );

  const removeGuardian = useCallback(
    async (memberId: string, guardianUserId: string) => {
      await savingMutation.mutateAsync(async () => {
        if (!orgId || !token) {
          throw new Error("Missing organization or auth token");
        }
        const result = await api.family.removeGuardian(
          orgId,
          memberId,
          guardianUserId,
          token
        );
        if (result.deletedMember) {
          setChildrenCache((prev) => prev.filter((c) => c.id !== memberId));
        } else {
          await invalidate();
        }
      });
    },
    [savingMutation, orgId, token, setChildrenCache, invalidate]
  );


  return {
    upsertChild,
    deleteChild,
    updateChildCircles,
    upsertSchoolFees,
    recordPayment,
    deletePayment,
    deleteSchoolFees,
    upsertFeeAccount,
    deleteFeeAccount,
    cancelFeeAccount,
    recordFeeAccountPayment,
    deleteFeeAccountPayment,
    markFeeAccountStatementSent,
    setFeeAccountPaidInFull,
    markStatementSent,
    setFeesPaidInFull,
    upsertExtracurricular,
    addExtracurriculars,
    deleteExtracurricular,
    upsertReportCard,
    deleteReportCard,
    addGuardian,
    removeGuardian,
    isSaving: savingMutation.isPending,
  };
}
